<template>
  <div class="home-view">
    <!-- 顶部横幅 -->
    <div class="banner-container">
      <div class="hero-banner">
        <!-- 响应式图片容器 -->
        <div class="banner-image-container">
          <img
              :src="compressedHomeImage"
              :alt="'首页横幅'"
              class="banner-image"
              :style="{ opacity: homeImageLoaded ? 1 : 0 }"
              @load="onHomeImageLoad"
          />
          <!-- 加载状态显示 -->
          <div v-if="!homeImageLoaded" class="image-loading">
            <div class="loading-spinner"></div>
          </div>
        </div>

        <!-- 横幅内容容器 -->

          <!-- 按钮容器 -->
          <div class="banner-button-container">
            <button class="cta-button" @click="goToPluginDetails(1978297871453697)">
              <span>查看详情</span>
            </button>
          </div>

      </div>
    </div>

    <!-- 插件列表 -->
    <div class="plugin-section">
      <div class="section-header">
        <h2>精选工具</h2>
      </div>
      <div class="plugin-grid">
        <div
            v-for="(plugin, index) in compressedPluginList"
            :key="plugin.id"
            class="plugin-card"
            @click="goToPluginDetails(plugin.id)"
            :style="{
              '--card-delay': `${index * 0.1}s`
            }"
        >
          <div class="card-background"></div>
          <div class="plugin-image-container">
            <img
                :src="plugin.compressedImage"
                :alt="plugin.name"
                class="plugin-image"
                :style="{ opacity: plugin.imageLoaded ? 1 : 0 }"
                @load="onPluginImageLoad(plugin.id)"
            />
            <div v-if="!plugin.imageLoaded" class="image-loading-small">
              <div class="loading-spinner-small"></div>
            </div>
            <div class="plugin-index">{{ index + 1 }}</div>
          </div>
          <div class="plugin-name">{{ plugin.name }}</div>
          <div class="card-hover-effect"></div>
        </div>
      </div>
    </div>

    <!-- 门户网站 -->
    <div class="portal-section">
      <div class="section-header">
        <h2>解决方案</h2>
      </div>

      <div class="portal-grid">
        <div class="portal-card" >
          <div class="portal-icon">
            <el-icon><Edit /></el-icon>
          </div>
          <h3>设计与创作</h3>
          <p>专业的包装设计、3D建模和创意工具，提升设计效率</p>
          <div class="portal-arrow">
            <el-icon><ArrowRight /></el-icon>
          </div>
        </div>

        <div class="portal-card" >
          <div class="portal-icon">
            <el-icon><Check /></el-icon>
          </div>
          <h3>质量验证</h3>
          <p>自动化校验、能效标签验证和合规性检查工具</p>
          <div class="portal-arrow">
            <el-icon><ArrowRight /></el-icon>
          </div>
        </div>

        <div class="portal-card" >
          <div class="portal-icon">
            <el-icon><Setting /></el-icon>
          </div>
          <h3>生产就绪</h3>
          <p>智能刀模、工艺文件和印刷就绪解决方案</p>
          <div class="portal-arrow">
            <el-icon><ArrowRight /></el-icon>
          </div>
        </div>

        <div class="portal-card" >
          <div class="portal-icon">
            <el-icon><Opportunity /></el-icon>
          </div>
          <h3>流程自动化</h3>
          <p>工作流自动化、批量处理和系统集成方案</p>
          <div class="portal-arrow">
            <el-icon><ArrowRight /></el-icon>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { Crop, DataAnalysis, CreditCard } from '@element-plus/icons-vue';
import { Edit, Check, Setting, Opportunity, ArrowRight } from '@element-plus/icons-vue';
import {
  compressImageFromUrl,
  batchCompressImages,
  revokeBlobUrl,
  revokeBlobUrls
} from '@/utils/imageCompress'; // 导入工具函数
import 'animate.css';

export default {
  name: 'HomeView',

  components: {
    Crop, DataAnalysis, CreditCard,
    Edit, Check, Setting, Opportunity, ArrowRight
  },

  setup() {
    const router = useRouter();

    // 响应式数据
    const homeImageLoaded = ref(false);
    const compressedHomeImage = ref('');

    // 存储所有压缩后的图片URL，用于清理
    const compressedImageUrls = reactive([]);

    // 插件列表数据
    const pluginList = reactive([
      {
        id: 1,
        name: "企业级二维码插件",
        image: require('@/assets/code.gif'),
        compressedImage: '',
        imageLoaded: false
      },
      {
        id: 2,
        name: "智能刀模生成插件",
        image: require('@/assets/mo.gif'),
        compressedImage: '',
        imageLoaded: false
      },
      {
        id: 3,
        name: "智能能效标签管理插件",
        image: require('@/assets/neng.gif'),
        compressedImage: '',
        imageLoaded: false
      },
      {
        id: 4,
        name: "美国版能效标签插件",
        image: require('@/assets/huang.gif'),
        compressedImage: '',
        imageLoaded: false
      },
    ]);

    // 加载并压缩首页背景图
    const loadAndCompressHomeImage = async () => {
      try {
        const imageUrl = require('@/assets/home1.gif');
        const compressedUrl = await compressImageFromUrl(imageUrl, {
          quality: 0.7,
          maxWidth: 2560,
          maxHeight: 1440
        });

        // 预加载图片以确保背景图能正常显示
        const img = new Image();
        img.onload = () => {
          compressedHomeImage.value = compressedUrl;
          homeImageLoaded.value = true;
        };
        img.src = compressedUrl;

        compressedImageUrls.push(compressedUrl);
      } catch (error) {
        console.error('首页图片压缩失败:', error);
        // 失败时使用原图
        compressedHomeImage.value = require('@/assets/home1.gif');
        homeImageLoaded.value = true;
      }
    };

    // 加载并压缩插件图片
    const loadAndCompressPluginImages = async () => {
      try {
        // 准备批量压缩的数据
        const imagesToCompress = pluginList.map(plugin => ({
          id: plugin.id,
          imageUrl: plugin.image
        }));

        // 批量压缩
        const compressedResults = await batchCompressImages(imagesToCompress, {
          quality: 0.6,
          maxWidth: 800,
          maxHeight: 600
        });

        // 更新插件列表
        compressedResults.forEach(result => {
          const plugin = pluginList.find(p => p.id === result.id);
          if (plugin) {
            plugin.compressedImage = result.compressedUrl;
            if (result.compressSuccess) {
              compressedImageUrls.push(result.compressedUrl);
            }
          }
        });
      } catch (error) {
        console.error('批量压缩插件图片失败:', error);
        // 失败时使用原图
        pluginList.forEach(plugin => {
          plugin.compressedImage = plugin.image;
        });
      }
    };

    // 图片加载完成处理
    const onHomeImageLoad = () => {
      homeImageLoaded.value = true;
    };

    const onPluginImageLoad = (pluginId) => {
      const plugin = pluginList.find(p => p.id === pluginId);
      if (plugin) {
        plugin.imageLoaded = true;
      }
    };

    // 清理压缩图片的Blob URL
    const cleanupCompressedImages = () => {
      if (compressedHomeImage.value && compressedHomeImage.value.startsWith('blob:')) {
        revokeBlobUrl(compressedHomeImage.value);
      }

      revokeBlobUrls(compressedImageUrls);
    };

    // 计算属性 - 获取压缩后的插件列表
    const compressedPluginList = pluginList;

    // 方法
    const goToPluginDetails = (pluginId) => {
      router.push({
        path: '/products',
        query: { id: pluginId }
      });
    };

    // 生命周期
    onMounted(async () => {
      // 并行加载所有图片
      await Promise.all([
        loadAndCompressHomeImage(),
        loadAndCompressPluginImages()
      ]);
    });

    // 组件卸载时清理
    onUnmounted(() => {
      cleanupCompressedImages();
    });

    return {
      homeImageLoaded,
      compressedHomeImage,
      compressedPluginList,
      onHomeImageLoad,
      onPluginImageLoad,
      goToPluginDetails
    };
  }
};
</script>

<style scoped>
/* 全局样式 */
.home-view {
  font-family: 'Segoe UI', 'Microsoft YaHei', sans-serif;
  color: #333;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%);
  min-height: 100vh;
  overflow-x: hidden;
}

/* 顶部横幅响应式容器 */
.banner-container {
  width: 100%;
  overflow: hidden;
  position: relative;
}

/* 顶部横幅 */
.hero-banner {
  background: transparent;
  color: white;
  padding: 0;
  text-align: left;
  position: relative;
  overflow: hidden;
  min-height: 450px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.banner-image {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 保持图片比例并覆盖整个容器 */
  object-position: center; /* 图片居中显示 */
  transition: opacity 0.3s ease-in-out;
}

/* 图片加载相关样式调整 */
.image-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8edff 100%);
  z-index: 1;
}

.banner-button-container {
  position: absolute;
  left: 126px;
  bottom: 60px; /* 距离底部一定距离 */
  z-index: 2; /* 确保按钮在图片之上 */
}

.cta-button {
  background:white;
  color: blue;
  font-weight: bold;
  border: none;
  padding: 14px 40px;
  font-size: 1.1rem;
  border-radius: 5px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(255, 126, 95, 0.3);
  z-index: 1;
}

.cta-button:hover {
  border-radius: 5px !important;
  background: #eb6e0d;
  transform: translateY(-5px);
  box-shadow: 0 15px 25px rgba(255, 126, 95, 0.4);
}


/* 插件列表部分 */
.plugin-section {
  padding: 5px 20px 20px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
}

.plugin-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(75, 108, 183, 0.3), transparent);
}

.section-header {
  text-align: center;
  margin-bottom: 30px;
  position: relative;
}

.section-header h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 15px;
  background: linear-gradient(45deg, #3651bf, #e267d8, #eb6e0d); /* 添加与图片中相同的渐变色 */
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  display: inline-block;
}

.section-header h2::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #4b6cb7, #ff7e5f);
  border-radius: 2px;
}

.section-header p {
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 20px auto 0;
  line-height: 1.6;
}

.plugin-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  margin-top: 40px;
}

.plugin-card {
  background: white;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  position: relative;
  opacity: 0;
  transform: translateY(30px);
  animation: cardFadeIn 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;

}

@keyframes cardFadeIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.plugin-card:hover {
  transform: translateY(-15px) scale(1.03);
  box-shadow: 0 20px 40px rgba(75, 108, 183, 0.2);
}

.card-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(75, 108, 183, 0.05) 0%, rgba(255, 126, 95, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 0;
}

.plugin-card:hover .card-background {
  opacity: 1;
}

.plugin-image-container {
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8edff 100%);
  z-index: 1;
}

.plugin-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.7s ease;
}

.plugin-card:hover .plugin-image {
  transform: scale(1.1);
}

.plugin-index {
  position: absolute;
  top: 0;
  left: 0;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #4b6cb7, #182848);
  color: white;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  font-weight: 700;
  font-size: 1.3rem;
  clip-path: polygon(0 0, 0% 100%, 100% 0);
  padding: 2px 0 0 8px;
  box-sizing: border-box;
  z-index: 2;
}

.plugin-name {
  padding: 15px 15px;
  text-align: center;
  font-weight: 600;
  color: #2c3e50;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
}

.plugin-card:hover .plugin-name {
  background: linear-gradient(135deg, #4b6cb7, #182848);
  color: white;
}

.card-hover-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(75, 108, 183, 0.1) 0%, rgba(255, 126, 95, 0.1) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 0;
  border-radius: 16px;
}

.plugin-card:hover .card-hover-effect {
  opacity: 1;
}

/* 门户网站区域 */
.portal-section {
  padding: 5px 20px 20px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  background: linear-gradient(135deg, #f3f6ff 0%, #fcf5fa 100%);
}

.portal-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(75, 108, 183, 0.3), transparent);
}

.portal-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;

}

.portal-card {
  background: white;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(240, 244, 255, 0.8);


}

.portal-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #4b6cb7, #ff7e5f);
  transform: scaleX(0);
  transition: transform 0.5s ease;
}

.portal-card:hover {
  transform: translateY(-15px);
  box-shadow: 0 20px 40px rgba(75, 108, 183, 0.2);
}

.portal-card:hover::before {
  transform: scaleX(1);
}

.portal-icon {
  width: 90px;
  height: 90px;
  margin: 0 auto 30px;
  background: linear-gradient(135deg, #4b6cb7, #182848);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease;
  position: relative;
  z-index: 1;
}

.portal-card:hover .portal-icon {
  transform: scale(1.1) rotate(5deg);
  background: linear-gradient(135deg, #ff7e5f, #feb47b);
  box-shadow: 0 10px 20px rgba(255, 126, 95, 0.3);
}

.portal-icon .el-icon {
  font-size: 2.8rem;
  color: white;
}

.portal-card h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #2c3e50;
  position: relative;
  z-index: 1;
}

.portal-card p {
  color: #666;
  line-height: 1.7;
  margin-bottom: 30px;
  font-size: 1rem;
  position: relative;
  z-index: 1;
}

.portal-arrow {
  width: 45px;
  height: 45px;
  margin: 0 auto;
  background: #f0f4ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease;
  position: relative;
  z-index: 1;
}

.portal-card:hover .portal-arrow {
  background: linear-gradient(135deg, #4b6cb7, #182848);
  transform: translateX(8px);
}

.portal-arrow .el-icon {
  color: #4b6cb7;
  transition: all 0.5s ease;
}

.portal-card:hover .portal-arrow .el-icon {
  color: white;
}

/* 滚动动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 添加滚动动画类 */
.animate-on-scroll {
  animation: fadeIn 0.8s ease forwards;
}

/* 平板设备 (768px - 1024px) */
@media (max-width: 1024px) {

  .banner-container {
    width: 100%;
    max-height: 120px;
    position: relative;
  }

  .banner-image-container {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
  }

  .banner-button-container {
    display: none;
  }

  .plugin-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .portal-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .section-header h2 {
    font-size: 2.2rem;
  }

  .plugin-image-container {
    height: 180px;
  }

  .portal-card {
    padding: 30px 20px;
  }

  .portal-icon {
    width: 70px;
    height: 70px;
    margin-bottom: 20px;
  }

  .portal-icon .el-icon {
    font-size: 2.2rem;
  }
}

/* 移动设备 (576px - 768px) */
@media (max-width: 768px) {
  .hero-banner {
    padding: 40px 15px;
    min-height: 300px;
    height: 40vh;
    text-align: center;
  }

  .banner-content {
    justify-content: center;
    padding-bottom: 30px;
  }

  .banner-button-container {
    text-align: center;
  }

  .cta-button {
    padding: 12px 30px;
    font-size: 1rem;
  }

  .plugin-section,
  .portal-section {
    padding: 5px 15px 20px;
  }

  .plugin-grid {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-top: 30px;
  }

  .portal-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .section-header h2 {
    font-size: 2rem;
  }

  .section-header h2::after {
    width: 60px;
    height: 3px;
  }

  .plugin-image-container {
    height: 200px;
  }

  .plugin-name {
    font-size: 1.1rem;
    padding: 12px 15px;
  }

  .portal-card {
    padding: 25px 15px;
  }

  .portal-card h3 {
    font-size: 1.3rem;
  }

  .portal-card p {
    font-size: 0.95rem;
  }
}

/* 小屏移动设备 (小于576px) */
@media (max-width: 576px) {
  .hero-banner {
    padding: 30px 10px;
    min-height: 250px;
    height: 35vh;
  }

  .banner-content {
    padding-bottom: 20px;
  }

  .cta-button {
    padding: 10px 25px;
    font-size: 0.95rem;
    width: 100%;
    max-width: 200px;
  }

  .plugin-section,
  .portal-section {
    padding: 5px 10px 15px;
  }

  .section-header {
    margin-bottom: 20px;
  }

  .section-header h2 {
    font-size: 1.8rem;
  }

  .plugin-grid {
    gap: 12px;
    margin-top: 25px;
  }

  .portal-grid {
    gap: 12px;
  }

  .plugin-image-container {
    height: 160px;
  }

  .plugin-name {
    font-size: 1rem;
    padding: 10px 12px;
  }

  .portal-card {
    padding: 20px 12px;
  }

  .portal-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 15px;
  }

  .portal-icon .el-icon {
    font-size: 1.8rem;
  }

  .portal-card h3 {
    font-size: 1.2rem;
    margin-bottom: 15px;
  }

  .portal-card p {
    font-size: 0.9rem;
    margin-bottom: 20px;
  }

  .portal-arrow {
    width: 40px;
    height: 40px;
  }
}

/* 超大屏幕 (大于1440px) */
@media (min-width: 1440px) {
  .banner-content {
    max-width: 1400px;
  }

  .plugin-section,
  .portal-section {
    max-width: 1400px;
  }
}

</style>

<style scoped>
/* 添加图片加载相关样式 */
.image-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8edff 100%);
  z-index: 1;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4b6cb7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.image-loading-small {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.loading-spinner-small {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4b6cb7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 确保图片有平滑的过渡 */
.banner-background-image,
.plugin-image {
  transition: opacity 0.3s ease-in-out;
}

</style>
